import { Link } from 'react-router-dom'

import { AuthShell } from '@/components/AuthShell'
import { useAuth } from '@/lib/auth'

/**
 * Short in-app privacy summary. The canonical policy lives on the marketing
 * site so it stays reachable without an account and can be linked from stores.
 */
export function Privacy() {
  const { isLoggedIn } = useAuth()

  const body = (
    <div className="space-y-4 text-sm text-ink">
      <p className="text-ink-muted">
        Last updated 21 August 2026. The full policy is at{' '}
        <a
          className="text-brand hover:underline"
          href="https://getreminded.app/privacy.html"
          target="_blank"
          rel="noreferrer"
        >
          getreminded.app/privacy.html
        </a>
        .
      </p>

      <section className="space-y-2">
        <h2 className="font-medium text-ink">What we store</h2>
        <ul className="list-disc space-y-1 pl-5 text-ink-muted">
          <li>Your email address and password (hashed by PocketBase).</li>
          <li>Records you add: names, providers, dates, costs, notes, reminder preferences.</li>
          <li>Reminder delivery history needed to avoid duplicate emails.</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h2 className="font-medium text-ink">How we use it</h2>
        <p className="text-ink-muted">
          Only to run the service: show your dashboard, send the reminder emails you
          asked for, and let you export or delete your data. We do not sell personal
          data or use it for advertising.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-medium text-ink">Who can see it</h2>
        <p className="text-ink-muted">
          Your records are isolated to your account at the database layer. Email is
          sent through our email provider. Hosting and backups are under our control;
          backups are encrypted.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-medium text-ink">Your rights</h2>
        <p className="text-ink-muted">
          Export your data anytime from Settings. Delete your account from Settings
          to remove your user record and cascaded items and reminder history.
        </p>
      </section>

      <section className="space-y-2">
        <h2 className="font-medium text-ink">Contact</h2>
        <p className="text-ink-muted">
          Privacy questions:{' '}
          <a className="text-brand hover:underline" href="mailto:privacy@getreminded.app">
            privacy@getreminded.app
          </a>
        </p>
      </section>

      {isLoggedIn ? (
        <p>
          <Link className="text-brand hover:underline" to="/settings">
            Back to Settings
          </Link>
        </p>
      ) : (
        <p>
          <Link className="text-brand hover:underline" to="/login">
            Back to sign in
          </Link>
        </p>
      )}
    </div>
  )

  if (isLoggedIn) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <h1 className="text-lg font-semibold text-ink">Privacy</h1>
        {body}
      </div>
    )
  }

  return (
    <AuthShell title="Privacy" subtitle="How Reminded handles your data.">
      {body}
    </AuthShell>
  )
}
